/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   maintenanceApi.ts
 *     Description     :   API service for maintenance operations
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-19
 *     Modified        :   2026-01-19 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.11.0 (Specification Testing Standards Edition)
 *     Feature         :   028-lakehouse-maintenance
 * =========================================================================================
 */

import { apiClient } from './apiClient';
import type {
  MaintenancePolicy,
  MaintenanceHistory,
  PaginatedResponse,
} from '../../../shared/types/maintenance';

// ============================================================================
// Types
// ============================================================================

export interface DashboardSummary {
  healthSummary: {
    healthy: number;
    degraded: number;
    unhealthy: number;
    total: number;
  };
  maintenanceStats: {
    pendingOperations: number;
    runningOperations: number;
    lastCompleted: Date | null;
  };
  recentActivity: MaintenanceHistory[];
}

export interface LakehouseWithHealth {
  id: string;
  name: string;
  workspaceId: string;
  workspaceName: string;
  capacityId: string;
  capacityType: string;
  clientId?: string;
  clientName?: string;
  healthScore: number;
  healthStatus: 'Healthy' | 'Degraded' | 'Unhealthy';
  lastHealthCheck: Date;
  lastMaintenanceDate?: Date;
  nextMaintenanceDate?: Date;
}

export interface MaintenanceHistoryFilters {
  lakehouseId?: string;
  operationType?: 'OPTIMIZE' | 'VACUUM' | 'ZORDER';
  status?: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  startDate?: Date;
  endDate?: Date;
}

export interface LakehouseFilters {
  healthStatus?: 'Healthy' | 'Degraded' | 'Unhealthy';
  capacityType?: string;
  clientId?: string;
  search?: string;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get dashboard summary with health stats and recent activity
 */
export async function getDashboardSummary(): Promise<DashboardSummary> {
  const response = await apiClient.get<DashboardSummary>('/api/v1/maintenance/dashboard');
  return response.data;
}

/**
 * Get paginated list of lakehouses with health data
 */
export async function getLakehousesWithHealth(
  page: number = 1,
  pageSize: number = 20,
  filters?: LakehouseFilters,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc'
): Promise<PaginatedResponse<LakehouseWithHealth>> {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  });

  if (filters?.healthStatus) params.append('healthStatus', filters.healthStatus);
  if (filters?.capacityType) params.append('capacityType', filters.capacityType);
  if (filters?.clientId) params.append('clientId', filters.clientId);
  if (filters?.search) params.append('search', filters.search);
  if (sortBy) params.append('sortBy', sortBy);
  if (sortOrder) params.append('sortOrder', sortOrder);

  const response = await apiClient.get<PaginatedResponse<LakehouseWithHealth>>(
    `/api/v1/maintenance/lakehouses?${params.toString()}`
  );
  return response.data;
}

/**
 * Get maintenance history with optional filters
 */
export async function getMaintenanceHistory(
  page: number = 1,
  pageSize: number = 20,
  filters?: MaintenanceHistoryFilters
): Promise<PaginatedResponse<MaintenanceHistory>> {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  });

  if (filters?.lakehouseId) params.append('lakehouseId', filters.lakehouseId);
  if (filters?.operationType) params.append('operationType', filters.operationType);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.startDate) params.append('startDate', filters.startDate.toISOString());
  if (filters?.endDate) params.append('endDate', filters.endDate.toISOString());

  const response = await apiClient.get<PaginatedResponse<MaintenanceHistory>>(
    `/api/v1/maintenance/history?${params.toString()}`
  );
  return response.data;
}

/**
 * Get maintenance history for a specific lakehouse
 */
export async function getLakehouseHistory(
  lakehouseId: string,
  page: number = 1,
  pageSize: number = 10
): Promise<PaginatedResponse<MaintenanceHistory>> {
  return getMaintenanceHistory(page, pageSize, { lakehouseId });
}

/**
 * Get single maintenance history record
 */
export async function getHistoryRecord(historyId: string): Promise<MaintenanceHistory> {
  const response = await apiClient.get<MaintenanceHistory>(`/api/v1/maintenance/history/${historyId}`);
  return response.data;
}

/**
 * Get all maintenance policies
 */
export async function getPolicies(
  page: number = 1,
  pageSize: number = 50
): Promise<PaginatedResponse<MaintenancePolicy>> {
  const response = await apiClient.get<PaginatedResponse<MaintenancePolicy>>(
    `/api/v1/maintenance/policies?page=${page}&pageSize=${pageSize}`
  );
  return response.data;
}

/**
 * Get single maintenance policy
 */
export async function getPolicy(policyId: string): Promise<MaintenancePolicy> {
  const response = await apiClient.get<MaintenancePolicy>(`/api/v1/maintenance/policies/${policyId}`);
  return response.data;
}

/**
 * Create maintenance policy
 */
export async function createPolicy(
  policy: Partial<MaintenancePolicy>
): Promise<MaintenancePolicy> {
  const response = await apiClient.post<MaintenancePolicy>('/api/v1/maintenance/policies', policy);
  return response.data;
}

/**
 * Update maintenance policy
 */
export async function updatePolicy(
  policyId: string,
  policy: Partial<MaintenancePolicy>
): Promise<MaintenancePolicy> {
  const response = await apiClient.put<MaintenancePolicy>(
    `/api/v1/maintenance/policies/${policyId}`,
    policy
  );
  return response.data;
}

/**
 * Delete maintenance policy
 */
export async function deletePolicy(policyId: string): Promise<void> {
  await apiClient.delete(`/api/v1/maintenance/policies/${policyId}`);
}

/**
 * Get effective policy for a lakehouse
 */
export async function resolvePolicy(lakehouseId: string): Promise<MaintenancePolicy> {
  const response = await apiClient.get<MaintenancePolicy>(
    `/api/v1/maintenance/policies/resolve/${lakehouseId}`
  );
  return response.data;
}

export default {
  getDashboardSummary,
  getLakehousesWithHealth,
  getMaintenanceHistory,
  getLakehouseHistory,
  getHistoryRecord,
  getPolicies,
  getPolicy,
  createPolicy,
  updatePolicy,
  deletePolicy,
  resolvePolicy,
};
